// components/calls/Transcripts.jsx
import React, { useState, useEffect } from "react";
import { ChevronDown, Calendar, Loader2, X, Filter } from "lucide-react";
import { TrendUp, ExportCurve, ImportCurve, Add } from "iconsax-reactjs";
import AskGenziButton from "./AskGenziButton";
import AdvancedFilters from "../common/AdvancedFilters";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import UploadCall from "./UploadCall";

const FILTER_STORAGE_KEY = "transcripts_filters";

const DEFAULT_FILTERS = {
  status: "all",
  sentiment: null,
  direction: null,
  qualificationLevel: null,
  isQualified: null,
  flagged: null,
  appointmentBooked: null,
  hasEmail: null,
  hasFullName: null,
  platform: null,
  extensionNumber: null,
  agentEmail: null, // Webex: filters by metadata.webexOwnerEmail
  minDuration: null,
  maxDuration: null,
  tags: null,
  startDate: null,
  endDate: null,
  search: "",
  page: 1,
  limit: 10,
  sortBy: "callStartTime",
  sortOrder: "DESC",
};

const getInitialFilters = () => {
  try {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) {
      // Restore saved filters but always reset page to 1 on return
      return { ...DEFAULT_FILTERS, ...JSON.parse(saved), page: 1 };
    }
  } catch {
    // Ignore parse errors, fall through to defaults
  }
  return { ...DEFAULT_FILTERS };
};

const Transcripts = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState(getInitialFilters);

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTranscripts: 0,
    averageDuration: "00:00:00",
    flaggedForReview: 0,
    positiveSentiment: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);

  // Persist filters to sessionStorage so they survive navigation
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Ignore storage errors (e.g. private browsing quota)
    }
  }, [filters]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchCalls();
    fetchAnalytics();
  }, [filters]);

  useEffect(() => {
    if (uploadSuccess && !showUploadModal) {
      fetchCalls();
      fetchAnalytics();
      setUploadSuccess(null);
    }
  }, [showUploadModal, uploadSuccess]);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.getCallFilterOptions();
      if (response.success) {
        setFilterOptions(response.options);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchCalls = async () => {
    try {
      setLoading(true);
      // Remove null/empty values from filters
      const cleanFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== null && value !== "" && value !== "all") {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );

      const response = await api.getCallRecordings(cleanFilters);

      if (response.success) {
        setCalls(response.calls || []);
        setPagination(
          response.pagination || {
            page: 1,
            pages: 1,
            total: 0,
          },
        );
      }
    } catch (error) {
      console.error("Error fetching calls:", error);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.getCallAnalytics();

      if (response.success && response.analytics) {
        const { overview, sentimentBreakdown } = response.analytics;

        const totalSentiments =
          sentimentBreakdown?.reduce(
            (acc, item) => acc + parseInt(item.count || 0),
            0,
          ) || 0;
        const positiveSentiments =
          sentimentBreakdown?.find((s) => s.averageSentiment === "positive")
            ?.count || 0;
        const positivePercentage =
          totalSentiments > 0
            ? Math.round((positiveSentiments / totalSentiments) * 100)
            : 0;

        setStats({
          totalTranscripts: overview?.totalCalls || 0,
          averageDuration: formatDuration(overview?.averageDuration || 0),
          flaggedForReview: overview?.flaggedCalls || 0,
          positiveSentiment: positivePercentage,
        });
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "00:00:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(secs).padStart(2, "0")}`;
  };

  const formatCallDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes === 0) return `${secs}s`;
    return `${minutes}m ${secs}s`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "text-green-400 bg-[#0C0E12]";
      case "processing":
      case "transcribing":
      case "analyzing":
        return "text-orange-400 bg-[#0C0E12]";
      case "failed":
        return "text-red-400 bg-[#0C0E12]";
      case "uploading":
      case "uploaded":
        return "text-blue-400 bg-[#0C0E12]";
      default:
        return "text-gray-400 bg-[#0C0E12]";
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1,
    }));
  };

  const handleQuickFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
      page: 1,
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleExportCalls = async () => {
    try {
      await api.exportCalls(filters);
    } catch (error) {
      console.error("Error exporting calls:", error);
    }
  };

  const handleViewCall = (callId) => {
    navigate(`/calls/${callId}`);
  };

  const handleSyncCallSwitch = async () => {
    setSyncing(true);
    try {
      const response = await api.syncCallSwitchRecordings({
        hours: 24,
        forceUpdate: false,
      });

      setUploadSuccess(
        `✅ Sync completed: ${response.totalSynced} recordings synced, ${
          response.errors?.length || 0
        } errors`,
      );

      fetchCalls();
      fetchAnalytics();

      if (response.errors && response.errors.length > 0) {
        console.log("Sync errors:", response.errors);
      }
    } catch (error) {
      console.error("Sync error:", error);
      setUploadSuccess(
        `❌ Sync failed: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setSyncing(false);
    }
  };

  const handleUploadSuccess = (message) => {
    setUploadSuccess(message);
    setShowUploadModal(false);
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
  };

  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) =>
      key !== "page" &&
      key !== "limit" &&
      key !== "sortBy" &&
      key !== "sortOrder" &&
      value !== null &&
      value !== "" &&
      value !== "all",
  ).length;

  const clearFilter = (key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === "status" ? "all" : null,
    }));
  };

  const clearAllFilters = () => {
    try {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } catch {
      // Ignore
    }
    setFilters({ ...DEFAULT_FILTERS });
  };

  const StatCard = ({ title, value, change }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[#94979C] text-[14px] xl:text-[14px] font-medium">
          {title}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div className="text-[#F7F7F7] text-[28px] xl:text-[20px] font-bold">
            {value}
          </div>
          <div className="flex items-center text-white text-sm xl:text-[10px] font-medium">
            <TrendUp className="mr-1 text-green-400 xl:w-5 xl:h-5" />
            {change}%
          </div>
        </div>
      </div>
    </div>
  );

  const CallRow = ({ call }) => {
    const formattedDate = new Date(call.callStartTime).toLocaleDateString(
      "en-US",
      {
        month: "long",
        day: "numeric",
        year: "numeric",
      },
    );

    const tags = call.insights?.tags || [];
    const displayTags = tags.slice(0, 2);
    const remainingTags = tags.length - 2;

    const getTagColor = (index) => {
      const colors = [
        "bg-[#053321] text-white",
        "bg-[#1F235B] text-white",
        "bg-[#2C1C5F] text-white",
      ];
      return colors[index % colors.length];
    };

    return (
      <tr className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
        <td className="py-4 px-4 text-white font-medium">
          {call.callerId || call.callerPhone || "Unknown"}
        </td>
        <td className="py-4 px-4 text-gray-400">
          {formatCallDuration(call.duration)}
        </td>
        <td className="py-4 px-4">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full border border-[#373A41] text-xs font-medium ${getStatusColor(
              call.status,
            )}`}
          >
            <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
            {call.status?.charAt(0).toUpperCase() + call.status?.slice(1)}
          </span>
        </td>
        <td className="py-4 px-4">
          <div className="flex flex-wrap gap-1">
            {displayTags.map((tag, idx) => (
              <span
                key={idx}
                className={`px-3 py-1 rounded-full text-xs font-medium ${getTagColor(
                  idx,
                )}`}
              >
                {tag}
              </span>
            ))}
            {remainingTags > 0 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-600 text-white">
                +{remainingTags}
              </span>
            )}
          </div>
        </td>
        <td className="py-4 px-4 text-gray-400">{formattedDate}</td>
        <td className="py-4 px-4 text-right">
          <button
            onClick={() => handleViewCall(call.id)}
            className="text-gray-400 hover:text-white transition-colors p-1 underline"
          >
            View
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Success Message */}
        {uploadSuccess && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-700 rounded-lg text-green-400 flex items-center justify-between">
            <span>{uploadSuccess}</span>
            <button
              onClick={() => setUploadSuccess(null)}
              className="text-green-400 hover:text-green-300"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Transcripts
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Manage all your audio files, summaries and AI insights.{" "}
              {pagination.total} total calls.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={handleExportCalls}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <ExportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Export</span>
            </button>

            <button
              onClick={handleSyncCallSwitch}
              disabled={syncing}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0 disabled:opacity-50"
            >
              <ImportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">
                {syncing ? "Syncing..." : "Sync CallSwitch"}
              </span>
            </button>

            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #7F56D936,
                  0px 8px 16px 0px #7F56D91F,
                  0px 2px 4px -8px #7F56D914,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
                borderImageSlice: "1",
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Upload audio</span>
            </button>
          </div>
        </div>

        {/* Search and Advanced Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by caller ID, phone, name, email, or summary..."
              value={filters.search}
              onChange={(e) =>
                handleQuickFilterChange("search", e.target.value)
              }
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          <button
            onClick={() => setShowAdvancedFilters(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
                0px 36px 32px -16px #7F56D936,
                0px 8px 16px 0px #7F56D91F,
                0px 2px 4px -8px #7F56D914,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
              borderImageSlice: "1",
            }}
          >
            <Filter size={20} />
            <span>Advanced Filters</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div className="mb-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-400 font-medium">
                Active filters:
              </span>
              {Object.entries(filters).map(([key, value]) => {
                if (
                  key === "page" ||
                  key === "limit" ||
                  key === "sortBy" ||
                  key === "sortOrder" ||
                  !value ||
                  value === "all" ||
                  value === ""
                ) {
                  return null;
                }

                let displayValue = value;
                let displayKey = key.replace(/([A-Z])/g, " $1").trim();

                // Format display values
                if (
                  key === "isQualified" ||
                  key === "appointmentBooked" ||
                  key === "hasEmail" ||
                  key === "hasFullName" ||
                  key === "flagged"
                ) {
                  displayValue = value === "true" ? "Yes" : "No";
                }

                if (key === "startDate" || key === "endDate") {
                  displayValue = new Date(value).toLocaleDateString();
                }

                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 border border-purple-600/30 rounded-full text-sm"
                  >
                    <span className="text-gray-300">
                      <span className="font-medium">{displayKey}:</span>{" "}
                      {displayValue}
                    </span>
                    <button
                      onClick={() => clearFilter(key)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
              <button
                onClick={clearAllFilters}
                className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 font-medium underline"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}

        {/* Quick Filters */}
        {/* <div className="flex flex-wrap gap-2 mb-6">
          <select
            value={filters.status}
            onChange={(e) => handleQuickFilterChange("status", e.target.value)}
            className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="processing">Processing</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={filters.sentiment || ""}
            onChange={(e) =>
              handleQuickFilterChange("sentiment", e.target.value || null)
            }
            className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors"
          >
            <option value="">All Sentiment</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
            <option value="neutral">Neutral</option>
          </select>

          <select
            value={filters.direction || ""}
            onChange={(e) =>
              handleQuickFilterChange("direction", e.target.value || null)
            }
            className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors"
          >
            <option value="">All Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>

          <select
            value={filters.qualificationLevel || ""}
            onChange={(e) =>
              handleQuickFilterChange(
                "qualificationLevel",
                e.target.value || null,
              )
            }
            className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors"
          >
            <option value="">All Qualification Levels</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="unqualified">Unqualified</option>
          </select>
        </div> */}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total transcripts"
            value={stats.totalTranscripts}
            change="10"
          />
          <StatCard
            title="Average duration"
            value={stats.averageDuration}
            change="10"
          />
          <StatCard
            title="Flagged for review"
            value={stats.flaggedForReview}
            change="10"
          />
          <StatCard
            title="Positive sentiment"
            value={`${stats.positiveSentiment}%`}
            change="10"
          />
        </div>

        {/* Table */}
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 mb-8">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-purple-500" size={40} />
              </div>
            ) : calls.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg mb-2">
                  No call recordings found
                </p>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-purple-400 hover:text-purple-300 underline"
                  >
                    Clear filters to see all calls
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full min-w-[800px]">
                <thead className="bg-[#13161B]">
                  <tr className="border-b border-[#22262F]">
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Caller ID
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Duration
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Status
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Tags
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Date
                    </th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium text-sm"></th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <CallRow key={call.id} call={call} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!loading && calls.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-gray-700 gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    handlePageChange(Math.max(1, pagination.page - 1))
                  }
                  disabled={pagination.page === 1}
                  className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    handlePageChange(
                      Math.min(pagination.pages, pagination.page + 1),
                    )
                  }
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <span className="text-gray-400 text-sm">
                Page {pagination.page} of {pagination.pages} ({pagination.total}{" "}
                total)
              </span>
            </div>
          )}
        </div>

        <AskGenziButton />
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-[#0C0E12] border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0C0E12] border-b border-gray-700 p-6 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Upload Call Recording
                  </h2>
                  <p className="text-gray-400 mt-1">
                    Upload audio files for transcription and analysis.
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white p-2 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div>
              <UploadCall
                onSuccess={handleUploadSuccess}
                onCancel={handleCloseModal}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <AdvancedFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClose={() => setShowAdvancedFilters(false)}
          filterOptions={filterOptions}
        />
      )}
    </div>
  );
};

export default Transcripts;
